import { FirebaseData } from "../General/FirebaseData";
import { FirebaseFile } from "../General/FirebaseFile";
import { PropertyData } from "../Property/PropertyData";
import { UnitData } from "../Property/Units/UnitData";
import { UserData } from "../User/UserData";
import { LocalData } from "../General/LocalData";
import { Timestamp, DocumentReference } from "firebase/firestore";
import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { SubtaskData } from "./SubtaskData";
import { Utilities } from "../../../utils/utilities";
import { DataCreator } from "../../../utils/dataCreator";
import { RelativeData, RelativeDataType } from "../General/RelativeData";
import { FirebaseEvents } from "../../../utils/firebaseEvents";
import { ChatData } from "../Chat/ChatData";
import CollectionLoader from "../General/CollectionLoader";
import DeviationExternalLinkData from "../ExternalLinks/DeviationExternalLinkData";
import { DeviationCostData, InvoicedToData } from "./DeviationCostData";
import { DeviationUpdateData } from "./DeviationUpdateData";
import { Role } from "../User/Role";
import { DeviationPriority } from "./DeviationPriority";
import { DeviationStatus } from "./DeviationStatus";
import { TaskType } from "./DeviationType";
import { DeviationVisibility, DeviationVisibilityHelper } from "./DeviationVisibility";
import { SeenByMember } from "./SeenByMember";
import { CompanyTaskTypeData } from "../Company/CompanyTaskTypeData";
import { DefaultTaskSettings, TaskSettings } from "./TaskSettings";
import { DeviationsLoader } from "to-be-removed/api/deviations/deviations.loader";

export class DeviationData extends FirebaseData {
  units: UnitData[] = [];
  user?: UserData;
  title = "";
  description = "";
  deviationNumber = "";

  taskTypeID: string = "deviation";

  /** @deprecated use taskTypeID instead */
  taskType: TaskType = "deviation";
  /** @deprecated use taskTypeID */
  customTaskType?: CompanyTaskTypeData;

  tags?: string[];
  status: DeviationStatus = DeviationStatus.NOT_STARTED;
  reportDate?: Date;
  lastUpdate?: Date;
  reportedByName = "";
  files: FirebaseFile[] = [];
  images: FirebaseFile[] = [];
  floors: number[] = [];
  placeDescription?: string;
  visibility: DeviationVisibility = DeviationVisibilityHelper.getStandard(undefined);
  startDate?: Date;
  endDate?: Date;
  contractorGroups: ContractorGroupData[] = [];
  responsibles: UserData[] = [];
  executors: UserData[] = [];
  priority?: DeviationPriority;
  seenBy: SeenByMember[] = [];
  archived = false;
  relatives: RelativeData[] = [];
  chats: ChatData[] = [];
  taskOrder: string[] = [];
  taskListNumbered = false;
  invoicedTo?: InvoicedToData;
  settings: TaskSettings = DefaultTaskSettings;

  externalLinks: CollectionLoader<DeviationExternalLinkData>;
  updates: CollectionLoader<DeviationUpdateData>;
  costs: CollectionLoader<DeviationCostData>;
  subtasks: CollectionLoader<SubtaskData>;

  totalLinksCreated = 0;

  constructor(id: string, collection: string, parent: FirebaseData | undefined) {
    super(id, collection, parent);
    this.costs = new CollectionLoader("costs", this, DeviationCostData);
    this.externalLinks = new CollectionLoader("externalLinks", this, DeviationExternalLinkData);
    this.updates = new CollectionLoader("updates", this, DeviationUpdateData);
    this.subtasks = new CollectionLoader("tasks", this, SubtaskData);
  }

  setup(data: any) {
    super.setup(data);

    this.units = [];
    if (Array.isArray(data.unitIDs)) {
      data.unitIDs.forEach((unitId: string) => {
        if (this.parent instanceof PropertyData) {
          this.units.push(this.parent.getUnitWithId(unitId));
        } else {
          this.units.push(LocalData.getUnitWithId(unitId));
        }
      });
    }

    // ReportDate
    if (data.reportDate instanceof Date) {
      this.reportDate = data.reportDate;
    } else if (data.reportDate as Timestamp) {
      this.reportDate = (data.reportDate as Timestamp).toDate();
    }

    // LastUpdate
    if (data.lastUpdate instanceof Date) {
      this.lastUpdate = data.lastUpdate;
    } else if (data.lastUpdate as Timestamp) {
      this.lastUpdate = (data.lastUpdate as Timestamp).toDate();
    }

    this.reportedByName = typeof data.reportedByName === "string" ? data.reportedByName : "";

    this.user = typeof data.userID === "string" ? LocalData.users.addData(data.userID) : undefined;

    if (data.customTaskType instanceof DocumentReference) {
      const taskTypeID = data.customTaskType.id;
      const companyID = data.customTaskType.parent.parent.id;
      this.customTaskType = LocalData.companies.addData(companyID).taskTypes.addData(taskTypeID);
    } else if (typeof data.customTaskType === "object" && data.customTaskType !== null && typeof data.customTaskType.path === "string") {
      const pathElements = data.customTaskType.path.split("/");
      if (pathElements.length >= 4) {
        const taskTypeID = pathElements[3];
        const companyID = pathElements[1];
        this.customTaskType = LocalData.companies.addData(companyID).taskTypes.addData(taskTypeID);
      }
    } else {
      this.customTaskType = undefined;
    }

    if (typeof data.taskTypeID === "string") {
      this.taskTypeID = data.taskTypeID;

      if (["deviation", "routine"].includes(data.taskTypeID)) {
        this.taskType = data.taskTypeID;
      } else {
        this.taskType = "custom";
      }
    } else {
      this.taskType = data.taskType === "deviation" || data.taskType === "routine" || data.taskType === "custom" ? data.taskType : "deviation";
      this.taskTypeID = this.taskType === "custom" ? (this.customTaskType?.id ?? "deviation") : this.taskType;
    }

    // TODO: remove after migration
    if (!this.taskType) {
      this.taskType = typeof data.type === "number" && data.type === 1 ? "routine" : "deviation";
    }

    this.title = typeof data.title === "string" ? data.title : "";
    this.description = typeof data.description === "string" ? data.description : "";
    this.deviationNumber = typeof data.deviationNumber === "string" ? data.deviationNumber : "";
    this.totalLinksCreated = typeof data.totalLinksCreated === "number" ? data.totalLinksCreated : 0;
    this.tags = Array.isArray(data.tags) ? data.tags.filter(Boolean) : [];
    this.placeDescription = typeof data.placeDescription === "string" ? data.placeDescription : "";
    this.status = typeof data.status === "number" ? DeviationStatus.getStatusWithId(data.status) : DeviationStatus.NOT_STARTED;
    this.archived = typeof data.archived === "boolean" ? data.archived : false;
    this.priority = typeof data.priority === "number" ? DeviationPriority.getPriorityWithId(data.priority) : undefined;
    this.taskListNumbered = typeof data.taskListNumbered === "boolean" ? data.taskListNumbered : false;

    this.settings =
      typeof data.settings === "object"
        ? {
            checklistRequired: typeof data.settings.checklistRequired === "boolean" ? data.settings.checklistRequired : false,
            checklistFieldsRequired: typeof data.settings.checklistFieldsRequired === "boolean" ? data.settings.checklistFieldsRequired : false,
            attachmentRequired: typeof data.settings.attachmentRequired === "boolean" ? data.settings.attachmentRequired : false,
            commentRequired: typeof data.settings.commentRequired === "boolean" ? data.settings.commentRequired : false,
          }
        : DefaultTaskSettings;

    if (
      typeof data.invoicedTo === "object" &&
      typeof data.invoicedTo.companyID === "string" &&
      typeof data.invoicedTo.invoiceReceiverID === "string"
    ) {
      this.invoicedTo = {
        company: LocalData.companies.addData(data.invoicedTo.companyID),
        invoiceReceiver: LocalData.companies.addData(data.invoicedTo.companyID).invoiceReceivers.addData(data.invoicedTo.invoiceReceiverID),
      };
    }

    this.relatives = [];
    if (typeof data.relatives === "object") {
      for (const relativeID of Object.keys(data.relatives)) {
        this.relatives.push(new RelativeData(relativeID, data.relatives[relativeID]));
      }
    }
    this.contractorGroups = Array.isArray(data.contractorGroups)
      ? data.contractorGroups.map((contractorID: string) => LocalData.contractorGroups.addData(contractorID))
      : [];
    this.responsibles = Array.isArray(data.responsibles) ? data.responsibles.map((userID: string) => LocalData.users.addData(userID)) : [];
    this.executors = Array.isArray(data.executors) ? data.executors.map((userID: string) => LocalData.users.addData(userID)) : [];
    this.floors = Array.isArray(data.floors) ? data.floors : [];
    this.taskOrder = Array.isArray(data.taskOrder) ? data.taskOrder : [];

    // Visibility
    if (this.isValidObject(data.visibility)) {
      this.visibility = {
        landlords: typeof data.visibility.landlords === "boolean" ? data.visibility.landlords : false,
        tenants: typeof data.visibility.tenants === "boolean" ? data.visibility.tenants : false,
        allTenants: typeof data.visibility.allTenants === "boolean" ? data.visibility.allTenants : false,
      };
    }

    // Files
    this.files = [];
    this.images = [];
    if (typeof data.files === "object") {
      for (const fileId in data.files) {
        const file = FirebaseFile.initialize(fileId, data.files[fileId]);
        file.setCreatedBy(this.createdBy, this.user, this.reportDate ?? this.createdBy?.date);

        if (file.isImage()) {
          this.images.push(file);
        } else {
          this.files.push(file);
        }
      }
    }

    // StartDate
    if (data.startDate as Timestamp) {
      try {
        this.startDate = (data.startDate as Timestamp).toDate();
      } catch (error) {
        this.startDate = undefined;
      }
    }
    if (data.startDate instanceof Date) {
      this.startDate = data.startDate;
    }

    // EndDate
    if (data.deadline as Timestamp) {
      try {
        this.endDate = (data.deadline as Timestamp).toDate();
      } catch (error) {
        this.endDate = undefined;
      }
    }
    if (data.deadline instanceof Date) {
      this.endDate = data.deadline;
    }

    // Seen by
    this.seenBy = [];
    if (this.isValidObject(data.seenBy)) {
      for (const userId in data.seenBy) {
        /* if (
           !this.user ||
           (userId !== this.user.id && userId !== LocalData.currentUser.id)
         ) {*/
        const userData = data.seenBy[userId];
        if (userData.date) {
          const date = new Date(userData.date.seconds * 1000);
          let seenByData: SeenByMember = {
            user: LocalData.users.addData(userId),
            date: date,
            viewing: typeof userData.viewing === "boolean" ? userData.viewing : undefined,
          };
          this.seenBy.push(seenByData);
          //  }
        }
      }
    }

    // Chats
    this.chats = [];
    if (this.isValidObject(data.chats)) {
      for (const chatId in data.chats) {
        const chatData = data.chats[chatId];
        const chat = LocalData.chats.addData(chatId);
        if (Array.isArray(chatData.members)) {
          chatData.members.forEach((memberId: string) => {
            chat.members.push(LocalData.users.addData(memberId));
          });
        }
        if (
          (this.parent instanceof PropertyData && this.parent.checkForLandlordRoles(LocalData.currentUser)) ||
          (Array.isArray(chatData.members) && chatData.members.includes(LocalData.currentUser.id))
        ) {
          this.chats.push(chat);
        }
      }
    }
  }

  addContractorGroup(group: ContractorGroupData) {
    if (!this.contractorGroups.includes(group)) {
      this.contractorGroups.push(group);
    }
  }

  delete() {
    return this.saveUpdate({
      deletedBy: DataCreator.getDeletedBy(),
    }).then(() => {
      DeviationsLoader.getInstance().removeDeviationData(this);
      return this;
    });
  }

  archive(): Promise<void> {
    const newArchived = !this.archived;
    const updateData: { [key: string]: any } = {
      archived: newArchived,
    };
    return this.sendDeviationUpdate(updateData, undefined).then(() => {
      this.archived = newArchived;
    });
  }

  setResponsibles(responsibles: UserData[]) {
    return this.sendDeviationUpdate(
      {
        responsibles: responsibles.map((r) => r.id),
      },
      undefined
    ).then(() => {
      this.responsibles = responsibles;
    });
  }

  setExecutors(executors: UserData[]) {
    return this.sendDeviationUpdate(
      {
        executors: executors.map((r) => r.id),
      },
      undefined
    ).then(() => {
      this.executors = executors;
    });
  }

  sendDeviationUpdate(newData: { [key: string]: any }, files: FirebaseFile[] | undefined): Promise<DeviationUpdateData> {
    const promises: Promise<void>[] = [];
    const updateID = FirebaseData.generateID();

    const deviationUpdateData: { [key: string]: any } = {
      date: Timestamp.fromDate(new Date()),
      createdBy: DataCreator.getCreatedBy(),
      ...newData,
    };

    if (!LocalData.currentUser.isExternal) deviationUpdateData.userID = LocalData.currentUser.id;

    if (this.parent instanceof PropertyData && files && files.length > 0) {
      const filesObject: { [key: string]: any } = {};
      for (const file of files) {
        const id = FirebaseData.generateID();
        file.id = id;
        const fileData: { [key: string]: any } = {
          type: file.fileType,
          name: file.title,
        };
        if (file.externalURL) {
          fileData.externalURL = file.externalURL;
        } else {
          file.path = `properties/${this.parent.id}/deviations/${this.id}/updates/${updateID}/${file.title}`;
          fileData.path = file.path;
        }
        filesObject[id] = fileData;

        if (file.path) {
          promises.push(
            new Promise((resolve) => {
              file.saveAndLoadURL((success) => {
                console.log("Uploaded deviation image: " + success);
                resolve();
              });
            })
          );
        }
      }
      deviationUpdateData.files = filesObject;
    }

    let deviationUpdate: DeviationUpdateData | undefined;
    promises.push(
      this.updates.createData(deviationUpdateData, updateID).then((update) => {
        deviationUpdate = update;

        if (this.parent instanceof PropertyData) {
          let eventRole = this.parent.checkForLandlordRoles(LocalData.currentUser)
            ? "1"
            : this.parent.checkForEntrepreneurRoles(LocalData.currentUser)
              ? "2"
              : "1";
          if (deviationUpdateData.status && this.reportDate) {
            FirebaseEvents.logEvent("deviation_status_change", {
              status: deviationUpdateData.status.toString(),
              previous_status: this.status.id.toString(),
              minutes_since_created: Utilities.secondsBetweenDates(this.reportDate, new Date()) / 60,
              role: eventRole,
              deviation_type: this.isRoutineTask() ? "1" : "0",
            });
          }
          FirebaseEvents.logEvent("deviation_update_sent", {
            update_type: FirebaseEvents.getDeviationUpdateType(deviationUpdateData),
            role: eventRole,
          });

          deviationUpdate.setup(deviationUpdateData);
          this.updates.documents = this.updates.documents.sort((a, b) => ((a.date ? a.date : new Date()) < (b.date ? b.date : new Date()) ? 1 : -1));

          deviationUpdate = deviationUpdate;
        }
      })
    );
    return Promise.all(promises).then(() => {
      if (deviationUpdate) {
        return deviationUpdate;
      }
      throw new Error("No deviation update created");
    });
  }

  isRoutineTask(): boolean {
    if (this.taskType === "routine") return true;
    //if (this.type === DeviationType.ROUTINE) return true;
    if (this.relatives.some((parent) => parent.type === RelativeDataType.ROUTINE)) return true;
    return false;
  }

  canViewDeviation(): boolean {
    if (this.deleted || this.rawData.deletedBy !== undefined || !this.loaded) return false;
    const property = this.parent instanceof PropertyData ? this.parent : undefined;
    const member = property?.members.getData(LocalData.currentUser.id);

    if (this.user === LocalData.currentUser) {
      return true;
    } else if (member?.roles.includes(Role.LANDLORD)) {
      return true;
    } else if (member?.isOperationManager) {
      return true;
    } else if (this.visibility.tenants) {
      for (const unit of this.units) {
        if (member?.units.includes(unit)) {
          return true;
        }
      }
    } else if (this.visibility.allTenants && member?.roles.includes(Role.RENTER)) {
      return true;
    }
    for (const group of this.contractorGroups) {
      if (group.members.hasData(LocalData.currentUser.id)) {
        return true;
      }
    }

    return false;
  }

  updateOrder(taskOrder: string[]): Promise<void | string[]> {
    return this.saveUpdate({ taskOrder }).then((result) => {
      this.taskOrder = result.taskOrder;
    });
  }

  updateTaskListNumbered(taskListNumbered: boolean): Promise<void | boolean> {
    return this.saveUpdate({ taskListNumbered }).then((result) => {
      this.taskListNumbered = result.taskListNumbered;
    });
  }
}
